import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { completeOAuthCallback } from '../lib/oauth';
import { setSession } from '../store/authSlice';
import { stashVerifyMfaToken } from './VerifyMfaPage';

/** Landing page for `/auth/callback` — the end of every OAuth round-trip. */
export default function OAuthCallbackPage() {
  const [params] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // The callback is single-use; StrictMode's double effect must not run it twice.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    completeOAuthCallback(params).then((outcome) => {
      if (outcome.kind === 'error') {
        setError(outcome.description ?? outcome.error);
        return;
      }
      if (outcome.kind === 'linked') {
        navigate(`/settings/auth-providers?linked=${encodeURIComponent(outcome.provider)}`, { replace: true });
        return;
      }
      const { result } = outcome;
      if ('verifyMfa' in result.tokens) {
        stashVerifyMfaToken(result.tokens.verifyMfa.token);
        navigate('/verify-mfa', { replace: true });
        return;
      }
      dispatch(setSession({ user: result.user, accessToken: result.tokens.access.token, refreshToken: result.tokens.refresh.token }));
      const next = result.returnUrl && result.returnUrl.startsWith('/') && !result.returnUrl.startsWith('//') ? result.returnUrl : '/account';
      navigate(next, { replace: true });
    });
  }, [params, dispatch, navigate]);

  if (error) {
    return (
      <main style={{ maxWidth: 360, margin: '4rem auto', fontFamily: 'system-ui, sans-serif' }}>
        <h1>Sign-in failed</h1>
        <p style={{ color: 'red' }}>{error}</p>
        <Link to="/login">Back to sign in</Link>
      </main>
    );
  }
  return <main>Signing you in…</main>;
}
